import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const PushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    // Check if we should show the prompt
    const checkNotificationStatus = async () => {
      // Only show for logged in users
      if (!user) return;
      
      // Check if browser supports notifications
      if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        console.log('Push notifications not supported');
        return;
      }
      
      // Check if already granted or denied
      if (Notification.permission === 'granted') {
        // Already have permission, try to subscribe
        await subscribeUser();
        return;
      }
      
      if (Notification.permission === 'denied') {
        console.log('Notifications denied by user');
        return;
      }
      
      // Check if user dismissed prompt recently (24h)
      const dismissedAt = localStorage.getItem('notificationPromptDismissed');
      if (dismissedAt) {
        const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
        if (hoursSinceDismissed < 24) {
          return;
        }
      }
      
      // Show prompt after 3 seconds
      setTimeout(() => setShowPrompt(true), 3000);
    };
    
    checkNotificationStatus();
  }, [user?.id]);

  const subscribeUser = async () => {
    try {
      // Get VAPID public key from server
      const keyResponse = await axios.get(`${API}/api/notifications/vapid-key`);
      const vapidPublicKey = keyResponse.data.publicKey;
      
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      await axios.post(`${API}/api/notifications/subscribe`, {
        user_id: user.id,
        subscription_json: subscription.toJSON()
      });
      
      console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push:', error);
      return false;
    }
  };

  const handleAllow = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        const success = await subscribeUser();
        if (success) {
          toast.success('¡Notificaciones activadas! Te avisaremos de nuevas ofertas.');
        }
      } else if (permission === 'denied') {
        toast.error('Notificaciones bloqueadas. Puedes activarlas desde la configuración del navegador.');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Error al activar notificaciones');
    } finally {
      setLoading(false);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('notificationPromptDismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">¿Activar notificaciones?</h3>
                    <p className="text-emerald-100 text-sm">No te pierdas ninguna oferta</p>
                  </div>
                </div>
                <button 
                  onClick={handleDismiss}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4">
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Recibe alertas instantáneas cuando haya nuevas ofertas cerca de ti. 
                <span className="text-emerald-600 font-medium"> Siempre puedes desactivarlas después.</span>
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleDismiss}
                  className="flex-1"
                >
                  Ahora no
                </Button>
                <Button
                  onClick={handleAllow}
                  disabled={loading}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? 'Activando...' : 'Activar'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
